import { useState, useEffect } from 'react';
import { feedbackApi } from '../../api/feedback.api';
import { useUI } from '../../store/UIContext';
import { Star } from '@phosphor-icons/react';

// Customer-only 1-5 star rating of the employee who answered this specific
// response - separate from the confirm/continue CSAT signal above it and
// from the AI satisfied/not_satisfied buttons. Never shown to employees;
// only their department manager sees the result (see
// ManagerResponseRatingsPage / getDepartmentResponseRatings).
export default function ResponseRating({ responseId, onHasRating }) {
  const { addToast } = useUI();
  const [existing, setExisting] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!responseId) return;
    setLoaded(false);
    feedbackApi.getMyResponseRating(responseId)
      .then((res) => {
        if (res.data?.rating) {
          setExisting(res.data);
          setRating(res.data.rating);
          setComment(res.data.comment || '');
          onHasRating?.(responseId, true);
        } else {
          onHasRating?.(responseId, false);
        }
      })
      .catch(() => { onHasRating?.(responseId, false); })
      .finally(() => setLoaded(true));
  }, [responseId]);

  const submit = async () => {
    if (!rating) {
      addToast('لطفاً امتیاز بدهید', 'error');
      return;
    }
    setSaving(true);
    try {
      await feedbackApi.submitResponseRating({ response_id: responseId, rating, comment: comment.trim() || undefined });
      setExisting({ rating, comment });
      onHasRating?.(responseId, true);
      addToast('امتیاز شما ثبت شد', 'success');
    } catch (e) {
      addToast(e.response?.data?.error || 'خطا در ثبت امتیاز', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!responseId || !loaded) return null;

  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(35,58,122,0.1)', padding: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
        {existing ? 'امتیاز شما به این پاسخ' : 'به این پاسخ چند ستاره می‌دهید؟'}
      </div>
      {!existing && (
        <p style={{ fontSize: 12, color: '#b45309', margin: '0 0 10px' }}>ثبت بازخورد برای هر پاسخ الزامی است.</p>
      )}
      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            onClick={() => setRating(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            style={{
              display: 'flex', background: 'none', border: 'none', cursor: 'pointer',
              transform: n <= (hover || rating) ? 'scale(1.08)' : 'scale(1)',
              transition: 'all 0.15s',
            }}
          >
            <Star size={24} weight={n <= (hover || rating) ? 'fill' : 'regular'} color={n <= (hover || rating) ? '#f59e0b' : '#cbd5e1'} />
          </button>
        ))}
      </div>
      {(rating > 0 || existing) && (
        <>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="نظر شما (اختیاری)"
            rows={2}
            style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 12, outline: 'none', fontFamily: 'inherit', resize: 'vertical', marginBottom: 10, boxSizing: 'border-box' }}
          />
          <button
            onClick={submit}
            disabled={saving}
            style={{
              padding: '8px 18px', borderRadius: 8, border: 'none', background: '#29AAE1', color: '#fff',
              fontSize: 12, fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit',
            }}
          >
            {existing ? 'به‌روزرسانی امتیاز' : 'ثبت امتیاز'}
          </button>
        </>
      )}
    </div>
  );
}
